import React, { useMemo, useState } from 'react'
import {
  Backdrop,
  Box,
  useTheme,
  Badge,
  Typography,
  Divider,
  darken,
  lighten,
  Popper,
  ClickAwayListener,
  Paper,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableSortLabel
} from '@mui/material'

import { alpha } from '@mui/material/styles'

import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Clear as ClearIcon
} from '@mui/icons-material'

import { Grid } from '@mui/system'

import { DashboardCounter } from './dashboardCounter'
import { ButtonBase } from '../boxButton'
import { parseGraphMonthLabel } from '../../utils'
import {
  formatMonthLabelKey,
  sumAiUnits,
  getBillableMode,
  splitBillableSupportUnits,
  computeDeficit,
  sumSupportUnits,
  sumVulnerabilityUnits,
  sumBucketChargesForGraph,
  countBucketChargesForGraph
} from '../../utils/dashboardMath'

// Chart tab as a table (+ client picker); used in data drawer and optional bottom sheet
const toNum = (v) => {
  const n = Number(v || 0)
  return Number.isFinite(n) ? n : 0
}

/** Drawer table: color these like billing — client accent when ≠ 0, muted when 0. */
const isChartDrawerSpAccentColumn = (tab, colKey, catNames) => {
  if (!catNames || !tab || !colKey) return false
  if (colKey === 'sp' || colKey === 'SP') return true
  if (tab === catNames.AI_SP_MONTHLY && ['billableSupport', 'nonBillableSupport', 'vulnUnits', 'upgradeUnits'].includes(colKey)) return true
  if (tab === catNames.AI_TRACKING && colKey === 'units') return true
  if (tab === catNames.AI_TICKET_SP && (colKey === 'units' || colKey === 'unitsPerItem')) return true
  return false
}

const formatMoney = (v, decimals = 2) => {
  const n = toNum(v)
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
}

const formatNumber = (v, decimals = 2) => {
  const n = toNum(v)
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
}

const addMonthsEnd = (startDate) => {
  const d = new Date(startDate)
  d.setMonth(d.getMonth() + 1)
  d.setDate(0)
  return d
}

const sortDrawerChartRows = (rows, cols, sort) => {
  const mul = sort.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    if (sort.field === 'client') {
      return mul * String(a._clientLabel || '').localeCompare(String(b._clientLabel || ''))
    }
    if (sort.field === 'start') {
      return mul * formatMonthLabelKey(a._labelStr || '').localeCompare(formatMonthLabelKey(b._labelStr || ''))
    }
    if (sort.field === 'end') {
      return mul * String(a.endLabel || '').localeCompare(String(b.endLabel || ''))
    }
    const va = Number(a.cells?.[sort.field])
    const vb = Number(b.cells?.[sort.field])
    if (Number.isFinite(va) && Number.isFinite(vb)) return mul * (va - vb)
    return mul * String(a.cells?.[sort.field] ?? '').localeCompare(String(b.cells?.[sort.field] ?? ''))
  })
}

const DrawerAccordionInlineStats = ({ lines, accentColor, bill }) => {
  if (!lines?.length) return null
  return lines.map((line, i) => (
    <React.Fragment key={line.key}>
      {i > 0 ? <Box component='span' sx={{ color: bill.muted, px: 0.75 }}>·</Box> : null}
      <Box component='span' sx={{ color: bill.muted, fontWeight: 700 }}>{line.label}:</Box>
      {' '}
      <Box
        component='span'
        sx={{
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: line.accent ? accentColor : bill.muted
        }}
      >
        {line.value}
      </Box>
    </React.Fragment>
  ))
}

const summarizeDrawerAccordionStats = (catNames, tab, rows, cols, billableMode = 'all') => {
  const sumKey = (k) => rows.reduce((s, r) => s + toNum(r.cells?.[k]), 0)
  const activeMonths = Math.max(1, rows.filter((r) => cols.some((c) => toNum(r.cells?.[c.key]) !== 0)).length)
  const lines = []
  const pushNum = (key, label, v, decimals = 2) => {
    const n = toNum(v)
    lines.push({ key, label, value: formatNumber(n, decimals), accent: n !== 0 })
  }
  const pushMoney = (key, label, v, decimals = 0) => {
    const n = toNum(v)
    lines.push({ key, label, value: formatMoney(n, decimals), accent: n !== 0 })
  }

  if (tab === catNames.SP_MONTHLY) {
    const d = sumKey('dollars')
    const sp = sumKey('sp')
    const eff = sp > 0 ? d / sp : 0
    pushMoney('totd', 'Total', d, 0)
    pushMoney('am', 'Avg / month', d / activeMonths, 0)
    pushNum('tsp', 'Total SP', sp, 0)
    pushMoney('eff', 'Effective $/SP', eff, 2)
    return lines
  }

  if (tab === catNames.AI_SP_MONTHLY) {
    const d = sumKey('dollars')
    const sp = sumKey('SP')
    const bs = sumKey('billableSupport')
    const nbs = sumKey('nonBillableSupport')
    const vu = sumKey('vulnUnits')
    const uu = sumKey('upgradeUnits')
    const def = sumKey('deficit')
    const eff = sp > 0 ? d / sp : 0
    const ratioRows = rows.filter((r) => toNum(r.cells?.SP) > 0)
    const avgRatioMonth = ratioRows.length > 0
      ? ratioRows.reduce((s, r) => s + toNum(r.cells?.dollars) / toNum(r.cells?.SP), 0) / ratioRows.length
      : 0
    pushMoney('tr', 'Total revenue', d, 0)
    pushNum('tsp', 'Total SP', sp, 2)
    pushMoney('eff', 'Effective $/SP', eff, 2)
    pushMoney('am', 'Avg revenue / month', d / activeMonths, 0)
    pushMoney('amsp', 'Avg $/SP per month', avgRatioMonth, 2)
    pushNum('bs', 'Billable support SP', bs, 2)
    pushNum('nbs', 'Non-billable support SP', nbs, 2)
    pushNum('vu', 'Vulnerability SP', vu, 2)
    pushNum('uu', 'Upgrade SP', uu, 2)
    if (billableMode === 'nonbillable') {
      pushMoney('def', 'Deficit (sum)', def, 2)
    }
    return lines
  }

  if (tab === catNames.AI_BILLING) {
    const d = sumKey('dollars')
    const nCharges = sumKey('chargeCount')
    pushMoney('aq', 'Avg QBO $/month', d / activeMonths, 0)
    pushMoney('tq', 'Total QBO $', d, 0)
    pushNum('nc', '# of charges', nCharges, 0)
    return lines
  }

  if (tab === catNames.BILLING) {
    const d = sumKey('dollars')
    pushMoney('tot', 'Total', d, 0)
    pushMoney('am', 'Avg / month', d / activeMonths, 0)
    return lines
  }

  if (tab === catNames.AI_TICKET_SP) {
    const u = sumKey('units')
    const it = sumKey('items')
    const upi = it > 0 ? u / it : 0
    pushNum('tu', 'Total units', u, 2)
    pushNum('ti', 'Total items', it, 0)
    pushNum('upi', 'Avg units / item', upi, 2)
    pushNum('am', 'Avg units / month', u / activeMonths, 2)
    pushNum('ami', 'Avg items / month', it / activeMonths, 2)
    return lines
  }

  if (tab === catNames.TRACKING) {
    const t = sumKey('sp')
    pushNum('tot', 'Total SP', t, 0)
    pushNum('am', 'Avg SP / month', t / activeMonths, 2)
    return lines
  }

  if (tab === catNames.AI_TRACKING) {
    const t = sumKey('units')
    pushNum('tot', 'Total SP (units)', t, 2)
    pushNum('am', 'Avg / month', t / activeMonths, 2)
    return lines
  }

  if (tab === catNames.TICKETS_MONTHLY) {
    const t = sumKey('ticketCount')
    pushNum('tot', 'Total tickets', t, 0)
    pushNum('am', 'Avg / month', t / activeMonths, 2)
    return lines
  }

  const d = sumKey('dollars')
  pushMoney('tot', 'Total', d, 0)
  pushMoney('am', 'Avg / month', d / activeMonths, 0)
  return lines
}

const DashboardAnalyzer = (componentProps) => {
  const { props: viewProps = {}, controls = {}, mode = 'bottom' } = componentProps

  const {
    activeTab = '',
    catOptions = {},
    catNames = {},
    graphStatistics = {},
    graphData = {},
    projectOptions = [],
    clientSelected = {},

    projectData = {},
    filtersSelected = {},
    billableSelected = {},
    graphFilters = {},
    selectedCharges = {},
    excludedChargeRowKeys = {},
    clientSettings = {}
  } = viewProps

  const {
    handleClearAll = () => { },
    handleClientChange = () => { },
    chartTableGroupBy: controlledChartGroupBy
  } = controls

  const theme = useTheme()

  const [drawerOpen, setDrawerOpen] = useState(false)

  const [clientPopperOpen, setClientPopperOpen] = useState(false)
  const [clientAnchorEl, setClientAnchorEl] = useState(null)

  const drawerGroupBy = controlledChartGroupBy ?? 'client'
  const [drawerSort, setDrawerSort] = useState({ field: 'start', dir: 'desc' })

  const handleDrawerSort = React.useCallback((field) => {
    setDrawerSort((prev) => {
      if (prev.field === field) {
        return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      }
      if (field === 'start' || field === 'end') return { field, dir: 'desc' }
      if (field === 'client') return { field, dir: 'asc' }
      return { field, dir: 'desc' }
    })
  }, [])

  const drawerChrome = useMemo(() => {
    if (mode !== 'drawer') return null
    const tabGraph = graphData?.[activeTab]
    const tabCfg = catOptions?.[activeTab]
    if (!activeTab || !tabGraph || !tabCfg) return null
    const datasets = (tabGraph.datasets || []).filter((d) => d?.label && d.label !== 'Average' && d.label !== 'Max')
    const rowCount = datasets.reduce((n, d) => n + (Array.isArray(d.data) ? d.data.length : 0), 0)
    const clientCount = datasets.length
    const bill = theme.custom.colors.billingDrawer
    return (
      <Box sx={{ flexShrink: 0, pb: 1.5, width: '100%' }}>
        <Typography variant='caption' sx={{ color: bill.muted, display: 'block' }}>
          {rowCount} row{rowCount === 1 ? '' : 's'} · {clientCount} client{clientCount === 1 ? '' : 's'}
        </Typography>
      </Box>
    )
  }, [mode, activeTab, graphData, catOptions, theme])

  const closeClientPopper = () => {
    setClientPopperOpen(false)
    setClientAnchorEl(null)
  }

  const toggleClientPopper = (e) => {
    if (clientPopperOpen) {
      closeClientPopper()
      return
    }
    setClientAnchorEl(e?.currentTarget || null)
    setClientPopperOpen(true)
  }

  const buildTypeFiltered = (monthlyData) => {
    const baseItems = [
      ...(Array.isArray(monthlyData?.Tickets) ? monthlyData.Tickets : []),
      ...(Array.isArray(monthlyData?.Upgrades) ? monthlyData.Upgrades : []),
      ...(Array.isArray(monthlyData?.Vulnerabilities) ? monthlyData.Vulnerabilities : [])
    ]

    const hasAnySupportTypeFilter = Object.values(filtersSelected || {}).some(Boolean)
    if (!hasAnySupportTypeFilter) return []

    return baseItems.filter((t) => {
      if (!t || typeof t !== 'object') return false
      const type = t.supportType
      if (!Object.prototype.hasOwnProperty.call(filtersSelected, type)) return false
      return Boolean(filtersSelected[type])
    })
  }

  const buildBillableFiltered = (items) => {
    const billableMode = getBillableMode(billableSelected)

    return (Array.isArray(items) ? items : []).filter((t) => {
      if (!t || typeof t !== 'object') return false

      if (t.supportType === graphFilters.VULNERABILITIES) return true

      if (billableMode === 'all') return true
      if (billableMode === 'billable') return t.isBillable === true
      if (billableMode === 'nonbillable') return t.isBillable !== true
      return true
    })
  }

  const getExcelColumnsForTab = (tab) => {
    if (tab === catNames.SP_MONTHLY) {
      return [
        { key: 'dollars', label: 'Dollars', format: (v) => formatMoney(v, 0) },
        { key: 'sp', label: 'SP', format: (v) => formatNumber(v, 0) },
        { key: 'ratio', label: '$/SP', format: (v) => formatMoney(v, 2) }
      ]
    }

    if (tab === catNames.BILLING) {
      return [{ key: 'dollars', label: 'Dollars', format: (v) => formatMoney(v, 0) }]
    }

    if (tab === catNames.AI_BILLING) {
      return [{ key: 'dollars', label: 'QBO Bill ($)', format: (v) => formatMoney(v, 0) }]
    }

    if (tab === catNames.TRACKING) {
      return [{ key: 'sp', label: 'SP', format: (v) => formatNumber(v, 0) }]
    }

    if (tab === catNames.TICKETS_MONTHLY) {
      return [{ key: 'ticketCount', label: 'Ticket Count', format: (v) => formatNumber(v, 0) }]
    }

    if (tab === catNames.AI_TRACKING) {
      return [{ key: 'units', label: 'Units', format: (v) => formatNumber(v, 2) }]
    }

    if (tab === catNames.AI_TICKET_SP) {
      return [
        { key: 'items', label: 'Items', format: (v) => formatNumber(v, 0) },
        { key: 'units', label: 'Units', format: (v) => formatNumber(v, 2) },
        { key: 'unitsPerItem', label: 'Units / Item', format: (v) => formatNumber(v, 2) }
      ]
    }

    if (tab === catNames.AI_SP_MONTHLY) {
      return [
        { key: 'dollars', label: 'Dollars', format: (v) => formatMoney(v, 0) },
        { key: 'billableSupport', label: 'Billable Support SP', format: (v) => formatNumber(v, 2) },
        { key: 'nonBillableSupport', label: 'Non-billable Support SP', format: (v) => formatNumber(v, 2) },
        { key: 'vulnUnits', label: 'Vulnerability SP', format: (v) => formatNumber(v, 2) },
        { key: 'upgradeUnits', label: 'Upgrade SP', format: (v) => formatNumber(v, 2) },
        { key: 'SP', label: 'Total SP', format: (v) => formatNumber(v, 2) },
        { key: 'ratio', label: '$/SP (or Deficit)', format: (v) => formatMoney(v, 2) },
        { key: 'deficit', label: 'Deficit', format: (v) => formatMoney(v, 2) }
      ]
    }

    return [{ key: 'value', label: 'Value', format: (v) => formatNumber(v, 2) }]
  }

  const buildExcelRow = (tab, projectName, labelStr, plottedValue) => {
    const monthKey = formatMonthLabelKey(labelStr)
    const monthlyData = projectData?.[projectName]?.[monthKey] || null

    const startDate = parseGraphMonthLabel(String(labelStr || ''))
    const endDate = startDate && !Number.isNaN(startDate.getTime()) ? addMonthsEnd(startDate) : null

    const startLabel = startDate && !Number.isNaN(startDate.getTime())
      ? startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : ''

    const endLabel = endDate && !Number.isNaN(endDate.getTime())
      ? endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : ''

    if (!monthlyData || typeof monthlyData !== 'object') {
      const cols = getExcelColumnsForTab(tab)
      const val = toNum(plottedValue)
      const defaultCells = { value: val }
      cols.forEach((c) => {
        if (defaultCells[c.key] !== undefined) return
        if (c.key === 'ratio' || c.key === 'value') defaultCells[c.key] = val
        else defaultCells[c.key] = 0
      })
      if (tab === catNames.AI_BILLING) defaultCells.chargeCount = 0
      return { startLabel, endLabel, cells: defaultCells }
    }

    const dollars = (tab === catNames.AI_BILLING || tab === catNames.AI_SP_MONTHLY)
      ? toNum(sumBucketChargesForGraph(monthlyData, selectedCharges, excludedChargeRowKeys))
      : toNum(monthlyData.Dollars)
    const sp = toNum(monthlyData.SP)
    const ticketCount = toNum(monthlyData.TicketCount)

    const typeFiltered = buildTypeFiltered(monthlyData)
    const billableFiltered = buildBillableFiltered(typeFiltered)

    const totalUnits = sumAiUnits(billableFiltered, graphFilters)

    const split = splitBillableSupportUnits(typeFiltered, graphFilters)
    const deficit = computeDeficit(dollars, split.billable, split.nonBillable)

    const upgradeUnits = (Array.isArray(typeFiltered) ? typeFiltered : []).reduce((sum, item) => {
      if (item?.supportType !== graphFilters.UPGRADES) return sum
      return sum + (toNum(item.revisedSP) || 0)
    }, 0)

    const vulnUnits = sumVulnerabilityUnits(typeFiltered, graphFilters)

    const billableMode = getBillableMode(billableSelected)

    const ratio = (() => {
      if (tab === catNames.SP_MONTHLY) return dollars / (sp > 0 ? sp : 1)

      if (tab === catNames.AI_SP_MONTHLY) {
        if (billableMode === 'nonbillable') return deficit
        return (totalUnits ? dollars : 0) / (totalUnits > 0 ? totalUnits : 1)
      }

      if (tab === catNames.AI_TICKET_SP) {
        const perItem = totalUnits / (billableFiltered.length || 1)
        return perItem
      }

      return toNum(plottedValue)
    })()

    const billableSupport = toNum(split.billable)
    const nonBillableSupport = toNum(split.nonBillable)

    const supportUnits = sumSupportUnits(billableFiltered, graphFilters)

    const chargeCount = tab === catNames.AI_BILLING
      ? countBucketChargesForGraph(monthlyData, selectedCharges, excludedChargeRowKeys)
      : 0

    return {
      startLabel,
      endLabel,
      cells: {
        value: toNum(plottedValue),
        dollars,
        sp,
        ...(tab === catNames.AI_SP_MONTHLY ? { SP: totalUnits } : {}),
        ticketCount,
        items: billableFiltered.length,
        units: totalUnits,
        supportUnits,
        unitsPerItem: billableFiltered.length ? totalUnits / billableFiltered.length : 0,
        ratio,
        deficit,
        billableSupport,
        nonBillableSupport,
        vulnUnits,
        upgradeUnits,
        chargeCount
      }
    }
  }

  const chartedInfo = useMemo(() => {
    const tabGraph = graphData?.[activeTab]
    const tabCfg = catOptions?.[activeTab]

    if (!activeTab || !tabGraph || !Array.isArray(tabGraph.datasets) || !Array.isArray(tabGraph.labels) || !tabCfg) {
      return []
    }

    const orderedSet = tabGraph.datasets.slice().sort((a, b) => {
      const aLabel = a?.label || ''
      const bLabel = b?.label || ''
      return String(aLabel).localeCompare(String(bLabel))
    })

    const dataLabels = tabGraph.labels
    const isSingleGraphed = orderedSet.length === 1
    const cols = getExcelColumnsForTab(activeTab)
    const drawerBillableMode = getBillableMode(billableSelected)

    const isDrawer = mode === 'drawer'
    const dd = theme.custom.colors.dataDrawer
    const bill = isDrawer ? theme.custom.colors.billingDrawer : null
    const labelPrimary = isDrawer ? dd.text : theme.palette.text.primary
    const labelSecondary = isDrawer ? dd.muted : theme.palette.text.secondary
    const avgColor = isDrawer ? '#81c784' : (theme.palette.success?.main ?? '#66bb6a')

    const datasetAccent = (idx) => {
      const palette = [
        theme.custom.keysys.blue,
        '#4dd0e1',
        '#ba68c8',
        '#ffb74d',
        '#81c784',
        '#f48fb1'
      ]
      return palette[idx % palette.length]
    }

    /** Match main chart / billing: `useDashboardGraph` uses clientSettings[label].color for series borders. */
    const accentForClientLabel = (clientLabel, paletteIdx) => {
      const fromSettings = clientLabel && clientSettings?.[clientLabel]?.color
      if (fromSettings) return fromSettings
      return datasetAccent(paletteIdx)
    }

    const monthPanelAccent = theme.custom.colors.billingDrawer.accent

    // Drawer: stack full-width panels per client / month (billing-style), not half-width columns.
    const drawerGridSize = { xs: 12 }
    const drawerFullBleed = isDrawer && orderedSet.length <= 1

    if (isDrawer && drawerGroupBy === 'month') {
      const labels = dataLabels || []
      const manyPanels = labels.length > 1 || orderedSet.length > 1
      const monthGridSize = { xs: 12 }

      return labels.map((labelStr, monthIdx) => {
        const rowsRaw = orderedSet
          .filter((ds) => ds && Array.isArray(ds.data) && monthIdx < ds.data.length)
          .map((datasetInfo) => {
            const v = datasetInfo.data[monthIdx]
            const ls = String(labelStr || '')
            const row = buildExcelRow(activeTab, datasetInfo.label, ls, v)
            return { ...row, _labelStr: ls, _clientLabel: datasetInfo.label }
          })
        const rows = sortDrawerChartRows(rowsRaw, cols, drawerSort)
        const accentColor = monthPanelAccent
        const parsed = parseGraphMonthLabel(String(labelStr || ''))
        const monthTitle = parsed && !Number.isNaN(parsed.getTime())
          ? parsed.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          : String(labelStr)
        const monthStatLines = summarizeDrawerAccordionStats(catNames, activeTab, rows, cols, drawerBillableMode)

        const headCellMonth = (field, label) => (
          <TableCell
            align='center'
            sortDirection={drawerSort.field === field ? drawerSort.dir : false}
            sx={{
              fontWeight: 800,
              color: bill.text,
              backgroundColor: `${bill.surface} !important`,
              borderColor: bill.border
            }}
          >
            <TableSortLabel
              active={drawerSort.field === field}
              direction={drawerSort.field === field ? drawerSort.dir : 'asc'}
              onClick={() => handleDrawerSort(field)}
              sx={{ color: bill.text, '& .MuiTableSortLabel-icon': { color: `${accentColor} !important` } }}
            >
              {label}
            </TableSortLabel>
          </TableCell>
        )

        return (
          <Grid item size={monthGridSize} key={`drawer-month-${formatMonthLabelKey(String(labelStr))}-${monthIdx}`} sx={{ minWidth: 0, width: '100%' }}>
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                width: '100%',
                borderRadius: 2,
                border: `1px solid ${bill.border}`,
                borderLeft: `4px solid ${accentColor}`,
                backgroundColor: alpha(bill.surface, 0.85),
                boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}`,
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': { minHeight: 52 }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                sx={{
                  backgroundColor: alpha(accentColor, 0.1),
                  borderBottom: `1px solid ${alpha(accentColor, 0.22)}`,
                  '& .MuiAccordionSummary-content': { flexWrap: 'wrap', width: '100%', mr: 1, alignItems: 'flex-start' }
                }}
              >
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 800, color: bill.text }}>
                    {monthTitle}
                  </Typography>
                  <Typography
                    component='div'
                    variant='caption'
                    sx={{
                      mt: 0.35,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      columnGap: 0.25,
                      rowGap: 0.35
                    }}
                  >
                    {rows[0]?.startLabel
                      ? (
                        <>
                          <Box component='span' sx={{ fontWeight: 700, color: bill.muted }}>Period:</Box>
                          <Box component='span' sx={{ color: bill.text, fontVariantNumeric: 'tabular-nums' }}>{rows[0].startLabel}</Box>
                          {monthStatLines.length > 0 ? <Box component='span' sx={{ color: bill.muted, px: 0.5 }}>·</Box> : null}
                        </>
                        )
                      : null}
                    <DrawerAccordionInlineStats lines={monthStatLines} accentColor={accentColor} bill={bill} />
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: alpha('#000', 0.15),
                  p: 0,
                  borderTop: `1px solid ${alpha(accentColor, 0.22)}`,
                  display: 'block'
                }}
              >
                <TableContainer
                  sx={{
                    maxHeight: manyPanels ? 'min(42vh, 380px)' : 'min(65vh, calc(100vh - 260px))',
                    overflow: 'auto',
                    width: '100%'
                  }}
                >
                  <Table stickyHeader size='small'>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: `${bill.surface} !important`, borderBottom: `2px solid ${alpha(accentColor, 0.4)}` }}>
                        {headCellMonth('client', 'Client')}
                        {headCellMonth('start', 'Start Date')}
                        {headCellMonth('end', 'End Date')}
                        {cols.map((c) => (
                          <TableCell key={`mh-${c.key}`} align='center' sortDirection={drawerSort.field === c.key ? drawerSort.dir : false} sx={{ fontWeight: 800, color: bill.text, backgroundColor: `${bill.surface} !important`, borderColor: bill.border }}>
                            <TableSortLabel active={drawerSort.field === c.key} direction={drawerSort.field === c.key ? drawerSort.dir : 'asc'} onClick={() => handleDrawerSort(c.key)} sx={{ color: bill.text, '& .MuiTableSortLabel-icon': { color: `${accentColor} !important` } }}>
                              {c.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r, idx) => (
                        <TableRow
                          key={`mrow-${monthIdx}-${idx}`}
                          sx={{
                            '&:nth-of-type(even)': { backgroundColor: alpha('#fff', 0.02) },
                            '&:hover': { backgroundColor: alpha(accentColor, 0.08) }
                          }}
                        >
                          <TableCell align='center' sx={{ color: bill.text, fontWeight: 700, borderColor: bill.border, fontSize: '0.8rem' }}>{r._clientLabel}</TableCell>
                          <TableCell align='center' sx={{ color: bill.muted, borderColor: bill.border, fontSize: '0.8rem' }}>{r.startLabel}</TableCell>
                          <TableCell align='center' sx={{ color: bill.muted, borderColor: bill.border, fontSize: '0.8rem' }}>{r.endLabel}</TableCell>
                          {cols.map((c) => {
                            const raw = r.cells?.[c.key]
                            const formatted = typeof c.format === 'function' ? c.format(raw) : String(raw ?? '')
                            const moneyLike = c.key === 'dollars' || c.key === 'deficit' || (c.key === 'ratio' && (activeTab === catNames.SP_MONTHLY || activeTab === catNames.AI_SP_MONTHLY))
                            const spAccentCol = isChartDrawerSpAccentColumn(activeTab, c.key, catNames)
                            const clientIdx = Math.max(0, orderedSet.findIndex((ds) => ds?.label === r._clientLabel))
                            const rowAccent = accentForClientLabel(r._clientLabel, clientIdx)
                            const accentForCell = moneyLike ? accentColor : (spAccentCol ? rowAccent : accentColor)
                            const highlightNumeric = moneyLike || spAccentCol
                            const nz = toNum(raw) !== 0
                            return (
                              <TableCell
                                key={`mcell-${c.key}`}
                                align='center'
                                sx={{
                                  fontVariantNumeric: 'tabular-nums',
                                  color: highlightNumeric ? (nz ? accentForCell : bill.muted) : bill.text,
                                  fontWeight: highlightNumeric && nz ? 700 : highlightNumeric ? 600 : 400,
                                  borderColor: bill.border,
                                  fontSize: '0.8rem'
                                }}
                              >
                                {formatted}
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )
      })
    }

    return orderedSet.reduce((arr, datasetInfo, datasetIdx) => {
      if (!datasetInfo || typeof datasetInfo !== 'object' || !datasetInfo.label || !Array.isArray(datasetInfo.data)) return arr

      const accentColor = accentForClientLabel(datasetInfo.label, datasetIdx)

      const rowsRaw = datasetInfo.data.map((v, idx) => {
        const labelStr = String(dataLabels?.[idx] || '')
        const row = buildExcelRow(activeTab, datasetInfo.label, labelStr, v)
        return { ...row, _labelStr: labelStr, _clientLabel: datasetInfo.label }
      })
      const rows = isDrawer ? sortDrawerChartRows(rowsRaw, cols, drawerSort) : rowsRaw

      const totalDataPoints = datasetInfo.data.filter((v) => Number.isFinite(v) && v > 0).length
      const totalDataValue = datasetInfo.data.reduce((sum, v) => (Number.isFinite(v) ? sum + v : sum), 0)
      const avg = totalDataPoints > 0 ? (totalDataValue / totalDataPoints) : 0

      const [firstLabel] = dataLabels
      const [lastLabel] = dataLabels.slice(-1)

      const startDate = parseGraphMonthLabel(String(firstLabel || ''))
      const endDate = parseGraphMonthLabel(String(lastLabel || ''))

      const formattedStartDate = startDate && !Number.isNaN(startDate.getTime())
        ? startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : ''

      const formattedEndDate = endDate && !Number.isNaN(endDate.getTime())
        ? endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : ''

      const gridItemSize = isDrawer ? drawerGridSize : (isSingleGraphed ? 'grow' : { xs: 12, sm: 12 })

      const headCellDrawer = (field, label) => (
        <TableCell
          align='center'
          sortDirection={drawerSort.field === field ? drawerSort.dir : false}
          sx={{
            fontWeight: 800,
            color: isDrawer ? bill.text : labelPrimary,
            backgroundColor: `${isDrawer ? bill.surface : dd.surface} !important`,
            borderColor: isDrawer ? bill.border : dd.border
          }}
        >
          <TableSortLabel
            active={drawerSort.field === field}
            direction={drawerSort.field === field ? drawerSort.dir : 'asc'}
            onClick={() => handleDrawerSort(field)}
            sx={{ color: isDrawer ? bill.text : labelPrimary, '& .MuiTableSortLabel-icon': { color: `${accentColor} !important` } }}
          >
            {label}
          </TableSortLabel>
        </TableCell>
      )

      const seriesStatLines = isDrawer ? summarizeDrawerAccordionStats(catNames, activeTab, rows, cols, drawerBillableMode) : []

      arr.push(
        <Grid item size={gridItemSize} key={`${datasetInfo.label}-${String(firstLabel)}-accordion`} sx={{ minWidth: 0, width: '100%' }}>
          <Accordion
            defaultExpanded
            disableGutters={isDrawer}
            elevation={0}
            sx={{
              width: '100%',
              borderRadius: 2,
              border: `1px solid ${isDrawer ? bill.border : alpha(accentColor, 0.35)}`,
              borderLeft: `4px solid ${accentColor}`,
              backgroundColor: isDrawer ? alpha(bill.surface, 0.85) : alpha(theme.custom.keysys.dark, 0.5),
              boxShadow: isDrawer
                ? `inset 0 1px 0 ${alpha('#fff', 0.04)}`
                : `0 2px 12px ${alpha('#000', 0.15)}, 0 0 0 1px ${alpha(accentColor, 0.1)}`,
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 52
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
              sx={{
                backgroundColor: isDrawer ? alpha(accentColor, 0.1) : alpha(accentColor, 0.15),
                borderBottom: `1px solid ${alpha(accentColor, isDrawer ? 0.22 : 0.25)}`,
                '&:hover': {
                  backgroundColor: alpha(accentColor, isDrawer ? 0.16 : 0.25)
                },
                ...(isDrawer ? { '& .MuiAccordionSummary-content': { flexWrap: 'wrap', gap: 1.25, width: '100%', mr: 1 } } : {})
              }}
            >
              {isDrawer
                ? (
                  <Box sx={{ width: '100%', minWidth: 0, pr: 0.5 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 800, color: bill.text }}>
                      {datasetInfo.label}
                    </Typography>
                    <Typography
                      component='div'
                      variant='caption'
                      sx={{
                        mt: 0.35,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        columnGap: 0.25,
                        rowGap: 0.35
                      }}
                    >
                      {formattedStartDate && formattedEndDate
                        ? (
                          <>
                            <Box component='span' sx={{ fontWeight: 700, color: bill.muted }}>Range:</Box>
                            <Box component='span' sx={{ color: bill.text }}>{`${formattedStartDate} — ${formattedEndDate}`}</Box>
                            {seriesStatLines.length > 0 ? <Box component='span' sx={{ color: bill.muted, px: 0.5 }}>·</Box> : null}
                          </>
                          )
                        : null}
                      <DrawerAccordionInlineStats lines={seriesStatLines} accentColor={accentColor} bill={bill} />
                    </Typography>
                  </Box>
                  )
                : (
                  <Grid container direction='row' sx={{ width: '100%' }} justifyContent='space-evenly' alignItems='center' columnSpacing={1}>
                    <Grid item size='auto' sx={{ marginRight: 'auto' }}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 800, color: labelPrimary }}>
                        {datasetInfo.label}
                      </Typography>
                    </Grid>

                    <Divider orientation='vertical' sx={{ borderColor: alpha(accentColor, 0.45) }} flexItem />

                    <Grid item size='auto'>
                      <Typography variant='overline' sx={{ color: labelSecondary }}>
                        {`${formattedStartDate} - ${formattedEndDate}`}
                      </Typography>
                    </Grid>

                    <Divider orientation='vertical' sx={{ borderColor: alpha(accentColor, 0.45) }} flexItem />

                    <Grid item size='auto'>
                      <Typography variant='overline' sx={{ color: avgColor, fontWeight: 700 }}>
                        {(tabCfg?.tabLabels?.averageLabel || 'Avg:')} {Number(avg).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                  )}
            </AccordionSummary>

            <AccordionDetails
              sx={{
                backgroundColor: isDrawer ? alpha('#000', 0.15) : alpha(theme.custom.keysys.dark, 0.25),
                p: isDrawer ? 0 : 2,
                borderTop: `1px solid ${alpha(accentColor, 0.22)}`,
                display: 'block'
              }}
            >
              <TableContainer
                component={isDrawer ? undefined : Paper}
                elevation={0}
                sx={{
                  maxHeight: isDrawer
                    ? (drawerFullBleed ? 'min(65vh, calc(100vh - 260px))' : 'min(42vh, 380px)')
                    : (isSingleGraphed ? 480 : '33vh'),
                  overflow: 'auto',
                  width: '100%',
                  borderRadius: isDrawer ? 0 : 2,
                  border: isDrawer ? 'none' : 'none',
                  backgroundColor: isDrawer ? 'transparent' : 'transparent',
                  boxShadow: 'none'
                }}
              >
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: isDrawer ? `${bill.surface} !important` : alpha(accentColor, 0.28),
                        borderBottom: `2px solid ${alpha(accentColor, isDrawer ? 0.4 : 0.5)}`
                      }}
                    >
                      {isDrawer
                        ? (
                          <>
                            {headCellDrawer('start', 'Start Date')}
                            {headCellDrawer('end', 'End Date')}
                            {cols.map((c) => (
                              <TableCell key={`col-${c.key}`} align='center' sortDirection={drawerSort.field === c.key ? drawerSort.dir : false} sx={{ fontWeight: 800, color: bill.text, backgroundColor: `${bill.surface} !important`, borderColor: bill.border }}>
                                <TableSortLabel active={drawerSort.field === c.key} direction={drawerSort.field === c.key ? drawerSort.dir : 'asc'} onClick={() => handleDrawerSort(c.key)} sx={{ color: bill.text, '& .MuiTableSortLabel-icon': { color: `${accentColor} !important` } }}>
                                  {c.label}
                                </TableSortLabel>
                              </TableCell>
                            ))}
                          </>
                          )
                        : (
                          <>
                            <TableCell align='center' sx={{ fontWeight: 800, color: labelPrimary, backgroundColor: isDrawer ? `${dd.surface} !important` : undefined, borderColor: isDrawer ? dd.border : undefined }}>Start Date</TableCell>
                            <TableCell align='center' sx={{ fontWeight: 800, color: labelPrimary, backgroundColor: isDrawer ? `${dd.surface} !important` : undefined, borderColor: isDrawer ? dd.border : undefined }}>End Date</TableCell>
                            {cols.map((c) => (
                              <TableCell key={`col-${c.key}`} align='center' sx={{ fontWeight: 800, color: labelPrimary, backgroundColor: isDrawer ? `${dd.surface} !important` : undefined, borderColor: isDrawer ? dd.border : undefined }}>
                                {c.label}
                              </TableCell>
                            ))}
                          </>
                          )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r, idx) => (
                      <TableRow
                        key={`${datasetInfo.label}-row-${idx}`}
                        sx={{
                          ...(isDrawer
                            ? {
                                '&:nth-of-type(even)': { backgroundColor: alpha('#fff', 0.02) },
                                '&:hover': { backgroundColor: alpha(accentColor, 0.08) }
                              }
                            : {
                                backgroundColor: idx % 2 === 0 ? 'transparent' : alpha(accentColor, 0.06),
                                '&:hover': { backgroundColor: alpha(accentColor, 0.12) }
                              })
                        }}
                      >
                        <TableCell align='center' sx={{ color: isDrawer ? bill.muted : labelSecondary, borderColor: isDrawer ? bill.border : undefined, fontSize: isDrawer ? '0.8rem' : undefined }}>{r.startLabel}</TableCell>
                        <TableCell align='center' sx={{ color: isDrawer ? bill.muted : labelSecondary, borderColor: isDrawer ? bill.border : undefined, fontSize: isDrawer ? '0.8rem' : undefined }}>{r.endLabel}</TableCell>
                        {cols.map((c) => {
                          const raw = r.cells?.[c.key]
                          const formatted = typeof c.format === 'function' ? c.format(raw) : String(raw ?? '')
                          const moneyLike = isDrawer && (c.key === 'dollars' || c.key === 'deficit' || (c.key === 'ratio' && (activeTab === catNames.SP_MONTHLY || activeTab === catNames.AI_SP_MONTHLY)))
                          const spAccentCol = isDrawer && isChartDrawerSpAccentColumn(activeTab, c.key, catNames)
                          const highlightNumeric = moneyLike || spAccentCol
                          const nz = toNum(raw) !== 0
                          return (
                            <TableCell
                              key={`${datasetInfo.label}-row-${idx}-cell-${c.key}`}
                              align='center'
                              sx={{
                                fontVariantNumeric: 'tabular-nums',
                                color: highlightNumeric ? (nz ? accentColor : bill.muted) : (isDrawer ? bill.text : labelPrimary),
                                fontWeight: highlightNumeric && nz ? 700 : highlightNumeric ? 600 : 400,
                                borderColor: isDrawer ? bill.border : undefined,
                                fontSize: isDrawer ? '0.8rem' : undefined
                              }}
                            >
                              {formatted}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )

      return arr
    }, [])
  }, [activeTab, graphData, catOptions, theme, projectData, filtersSelected, billableSelected, graphFilters, catNames, mode, drawerGroupBy, drawerSort, handleDrawerSort, clientSettings, selectedCharges, excludedChargeRowKeys])

  if (mode === 'drawer') {
    return (
      <Box
        sx={{
          p: 0,
          backgroundColor: 'transparent'
        }}
      >
        {drawerChrome}
        <Grid
          container
          direction='row'
          rowSpacing={2}
          columnSpacing={2}
          alignItems='stretch'
          justifyContent='flex-start'
          sx={{ width: '100%', boxSizing: 'border-box' }}
        >
          {chartedInfo}
        </Grid>
      </Box>
    )
  } else {
    return (
      <>
        <Backdrop sx={{ zIndex: 1300 }} open={drawerOpen} onClick={() => setDrawerOpen(false)} />

        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: darken(theme.custom.keysys.dark, 0.2),
            boxShadow: 6,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            transition: 'height 0.3s ease',
            height: drawerOpen ? '70vh' : '10vh',
            overflowX: 'hidden',
            overflowY: drawerOpen ? 'scroll' : 'hidden',
            zIndex: 1300
          }}
        >
          <Grid
            wrap='nowrap'
            container
            direction='row'
            alignItems='center'
            columnSpacing={2}
            sx={{
              padding: theme.spacing(2.5, 2),
              position: 'sticky',
              top: 0,
              backgroundColor: theme.custom.keysys.dark
            }}
          >
            <Grid item sx={{ marginRight: 'auto' }}>
              <Grid
                container
                item
                direction='row'
                columnSpacing={2}
                sx={{
                  border: `${theme.spacing(0.25)} solid ${theme.palette.background.paper}`,
                  boxShadow: `0px 0px 5px 5px ${alpha(theme.palette.background.paper, 0.4)}`,
                  padding: theme.spacing(1),
                  borderRadius: theme.shape.borderRadius
                }}
              >
                <DashboardCounter
                  activeTab={activeTab}
                  catOptions={catOptions}
                  catNames={catNames}
                  graphStatistics={graphStatistics}
                />
              </Grid>
            </Grid>

            <Grid item>
              <Badge
                badgeContent={graphData?.[activeTab]?.datasets?.length || 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: theme.spacing(2),
                    minHeight: theme.spacing(2),
                    height: 'auto !important',
                    borderRadius: '50%',
                    padding: theme.spacing(0.75, 1.25),
                    boxShadow: `0px 0px 4px 4px ${alpha(theme.custom.keysys.blue, 0.2)},0px 0px 5px 4px ${alpha(theme.custom.keysys.blue, 0.14)},0px 0px 10px 4px ${alpha(theme.custom.keysys.blue, 0.12)}`,
                    backgroundColor: darken(theme.custom.keysys.blue, 0.2)
                  }
                }}
              >
                <ButtonBase
                  onClick={toggleClientPopper}
                  sx={{ boxShadow: `0px 0px 5px 5px ${alpha(darken(theme.custom.keysys.blue, 0.4), 0.4)}` }}
                >
                  <Grid container item direction='row' columnSpacing={2}>
                    <Grid item><Typography variant='button'>Clients Graphed</Typography></Grid>

                    <Divider flexItem orientation='vertical' sx={{ borderRight: `${theme.spacing(0.25)} solid ${theme.palette.background.paper}` }} />

                    <Grid item>
                      {clientPopperOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Grid>
                  </Grid>
                </ButtonBase>
              </Badge>

              <Popper open={clientPopperOpen} anchorEl={clientAnchorEl} placement='top' style={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={closeClientPopper}>
                  <Paper sx={{ padding: theme.spacing(1.5), overflow: 'hidden' }}>
                    <Grid container direction='row'>
                      <Grid container item direction='column' rowSpacing={1} sx={{ width: '100%' }}>

                        <Grid item container direction='row'>
                          <ButtonBase
                            onClick={() => {
                              handleClearAll()
                              closeClientPopper()
                            }}
                            sx={{
                              padding: theme.spacing(1, 2),
                              backgroundColor: alpha(darken(theme.palette.error.main, 0.2), 0.8),
                              boxShadow: `0px 0px 5px 5px ${alpha(darken(theme.palette.error.main, 0.2), 0.3)}`,
                              '&:hover': { backgroundColor: alpha(darken(theme.palette.error.main, 0.3), 0.65) }
                            }}
                          >
                            <Grid container item direction='row' columnSpacing={2}>
                              <Grid item><ClearIcon /></Grid>

                              <Divider flexItem orientation='vertical' sx={{ borderRight: `${theme.spacing(0.25)} solid ${theme.palette.background.paper}` }} />

                              <Grid item><Typography variant='button'>Clear</Typography></Grid>
                            </Grid>
                          </ButtonBase>
                        </Grid>

                        <Divider orientation='horizontal' flexItem sx={{ borderBottom: `${theme.spacing(0.25)} solid ${theme.palette.text.disabled}` }} />

                        <Grid item container direction='row' sx={{ overflowY: 'scroll', maxHeight: '20vh' }}>
                          <FormControl component='fieldset' variant='standard'>
                            <FormGroup>
                              {[...(Array.isArray(projectOptions) ? projectOptions : [])]
                                .sort((a, b) => {
                                  const aSelected = clientSelected?.[a] || false
                                  const bSelected = clientSelected?.[b] || false
                                  if (aSelected && !bSelected) return -1
                                  if (!aSelected && bSelected) return 1
                                  return String(a || '').localeCompare(String(b || ''))
                                })
                                .map((clientName) => {
                                  if (typeof clientName !== 'string' || !clientName) return null
                                  return (
                                    <FormControlLabel
                                      key={`client-${clientName}`}
                                      control={
                                        <Checkbox
                                          name={clientName}
                                          checked={clientSelected?.[clientName] || false}
                                          onChange={handleClientChange}
                                        />
                                      }
                                      label={clientName}
                                    />
                                  )
                                })}
                            </FormGroup>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </Grid>

            <Grid item>
              <ButtonBase
                onClick={() => setDrawerOpen((v) => !v)}
                sx={{
                  padding: theme.spacing(1.5, 2),
                  backgroundColor: alpha(lighten(theme.custom.keysys.blue, 0.4), 0.4),
                  boxShadow: `0px 0px 5px 5px ${alpha(lighten(theme.custom.keysys.blue, 0.3), 0.3)}`,
                  '&:hover': { backgroundColor: alpha(lighten(theme.custom.keysys.blue, 0.4), 0.55) }
                }}
              >
                <Grid container item direction='row' columnSpacing={2}>
                  <Grid item><Typography variant='button'>Data Graphed</Typography></Grid>

                  <Divider flexItem orientation='vertical' sx={{ borderRight: `${theme.spacing(0.25)} solid ${theme.palette.background.paper}` }} />

                  <Grid item>
                    {drawerOpen ? <CloseIcon /> : <ExpandMoreIcon />}
                  </Grid>
                </Grid>
              </ButtonBase>
            </Grid>
          </Grid>

          <Box
            sx={{
              padding: 2,
              backgroundColor: alpha(theme.custom.keysys.dark, 0.6),
              borderTop: `1px solid ${alpha(theme.custom.keysys.blue, 0.2)}`
            }}
          >
            <Grid container direction='row' rowSpacing={2} columnSpacing={2} alignItems='stretch' justifyContent='space-between'>
              {chartedInfo}
            </Grid>
          </Box>
        </Box>
      </>
    )
  }
}

export { DashboardAnalyzer }
